/**
 * authenticated-fetch.ts -- the session-aware fetch wrapper (TG-3,
 * ADR-20261015-6576, AC-25).
 *
 * Split out of api.ts so the plain, single-attempt HTTP functions there
 * (requestSignIn, redeemMagicLink, refreshSession) stay free of the
 * retry, single-flight and session-ended bookkeeping this module owns.
 * `submitRenewal` lives here rather than in api.ts for the same reason --
 * it is the one caller of `authenticatedFetch` in this feature's public
 * surface, and keeping it here keeps the import edge between this file
 * and api.ts one-directional rather than circular.
 */
import { clearSession, getSessionId, getSessionToken, notifySessionEnded } from "./token";
import { refreshSession } from "./api";

const JSON_HEADERS = { "Content-Type": "application/json" } as const;

function readStringField(body: unknown, field: string): string | null {
  if (typeof body === "object" && body !== null && field in body) {
    const value = (body as Record<string, unknown>)[field];
    if (typeof value === "string") {
      return value;
    }
  }
  return null;
}

async function readJsonBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/** ADR-20261015-6576: 0.5s, 1s, 2s -- three retries of a refresh that
 * came back `retryable` (a 5xx, a 429, a gateway error or a network
 * failure), never more and never fewer. */
const REFRESH_RETRY_DELAYS_MS = [500, 1000, 2000] as const;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * ADR-20261015-6576 (AC-25): the session-ended view renders once per
 * ended session, not once per failed request that discovers it is ended.
 * An in-memory sentinel, NEVER a browser store -- `no-restricted-syntax`/
 * `portal-storage/no-storage-type-access` (ADR-20260826-84dz) ban
 * `sessionStorage`/`localStorage`/`indexedDB` from every first-party
 * module, with no carve-out for a sentinel that carries no exposure or
 * renewal data; `sid` (session plumbing, never customer data) does not
 * need to survive a reload in any case, since a reload drops the
 * in-memory session token this module itself depends on, so a fresh page
 * load always starts with a clean notified-sids set regardless of
 * whether this lived in memory or in storage. Keyed by the ending
 * session's own `sid` (read BEFORE `clearSession()` wipes the in-memory
 * token that carries it), so a second 401 for the SAME already-ended
 * session is a silent no-op here rather than a second notification and a
 * second navigation; a genuinely different session (a fresh sign-in)
 * always gets a fresh `sid` and is never suppressed by an earlier one.
 */
const notifiedEndedSessionIds = new Set<string | null>();

function notifySessionEndedOnce(sid: string | null): void {
  if (notifiedEndedSessionIds.has(sid)) {
    return;
  }
  notifiedEndedSessionIds.add(sid);
  notifySessionEnded();
}

/** Test-only reset of {@link notifiedEndedSessionIds} -- this module-scope
 * set is deliberately NOT reset by `clearSession()` (a sign-out must not
 * let the SAME just-ended session re-notify), so a test suite that
 * exercises more than one "ended" scenario across cases sharing the same
 * `sid` (most test JWTs carry none, so they all collide on `null`) needs
 * an explicit reset between cases -- exactly what `clearSession()`
 * itself must never provide for production code. */
export function __resetNotifiedEndedSessionIdsForTest(): void {
  notifiedEndedSessionIds.clear();
}

let inFlightRefresh: Promise<boolean> | null = null;

/**
 * Retries a `retryable` refresh outcome up to three times, at the
 * 0.5s/1s/2s backoff ADR-20261015-6576 names, and ends the session only
 * when `refreshSession` itself reports the definitive `"ended"` (AC-25:
 * "the session ends only when the refresh endpoint returns 401").
 * Exhausting every retry without either a success or an `"ended"` leaves
 * the session exactly as it was -- neither cleared nor renewed -- so a
 * still-recoverable outage never costs the customer their session; the
 * caller's own request simply fails this one time and tries again on its
 * own next trigger.
 */
/** OBJ-C-8/OBJ-R-7 (m1-declaration-next-steps, debate turn 2): incremented every time
 * {@link refreshWithRetry} actually mints a fresh token -- whether triggered reactively by an
 * `authenticatedFetch` 401 (for example a step-route guard loader's own ungated read) or by
 * `bootstrapSessionFromRefreshCookie`'s own cold-load call. `RequireSession` (App.tsx) uses this
 * to detect a refresh that happened BEFORE its own mount and that it has not yet accounted for --
 * distinct from, and a superset of, "no in-memory token at mount", which alone misses exactly the
 * race this exists to catch: a loader's own refresh silently warms the token ahead of
 * `RequireSession` mounting, so token presence at mount can no longer tell a true cold load from
 * an ordinary already-warm in-app navigation. A token set by some OTHER path (redeem, explicit
 * sign-in) never increments this and is correctly left alone -- it is not a cold-load reload. */
let refreshCompletionCount = 0;

export function getRefreshCompletionCount(): number {
  return refreshCompletionCount;
}

/** Test-only reset of {@link refreshCompletionCount}, same convention as
 * {@link __resetNotifiedEndedSessionIdsForTest} above -- a real page load starts this at 0 for
 * free; a test harness sharing one module instance across `it()` blocks does not. */
export function __resetRefreshCompletionCountForTest(): void {
  refreshCompletionCount = 0;
}

async function refreshWithRetry(): Promise<boolean> {
  const sidBeforeAttempt = getSessionId();
  for (let attempt = 0; ; attempt += 1) {
    const outcome = await refreshSession();
    if (outcome === "refreshed") {
      refreshCompletionCount += 1;
      return true;
    }
    if (outcome === "ended") {
      clearSession();
      notifySessionEndedOnce(sidBeforeAttempt);
      return false;
    }
    const nextDelayMs = REFRESH_RETRY_DELAYS_MS[attempt];
    if (nextDelayMs === undefined) {
      return false;
    }
    await delay(nextDelayMs);
  }
}

/**
 * ADR-20261015-6576: "one in-flight refresh promise shared inside
 * authenticatedFetch" -- every concurrent `authenticatedFetch` call that
 * hits a 401 at the same moment shares this ONE refresh-and-retry
 * sequence rather than each starting its own. Reset to `null` once the
 * sequence settles (success, ended, or retries exhausted), so the NEXT
 * 401 starts a fresh sequence rather than replaying a stale result.
 */
function refreshOnce(): Promise<boolean> {
  inFlightRefresh ??= refreshWithRetry().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

/**
 * ADR-20261016-r4bn (TG-8): the single entry point `RequireSession`
 * (App.tsx) calls at mount on a cold load -- a guarded route mounting with
 * no in-memory token at all, which happens on a full page reload or a
 * brand-new tab that still carries a live `__Host-sbr_refresh` cookie.
 * Shares the SAME single-flight sequence a reactive 401 uses
 * (`refreshOnce`), so a cold-load bootstrap racing a concurrent
 * `authenticatedFetch` 401 (for example two tabs opened at once) never
 * starts a second refresh request -- there is exactly one in-flight
 * refresh promise at a time, whichever caller started it. Resolves once
 * that shared sequence settles (success, ended, or AC-25's retries
 * exhausted); the caller reads the resulting session state for itself
 * (`getCurrentRawClaim`) rather than trusting this function's boolean,
 * since a concurrent sign-out or sign-in between settling and the
 * caller's own re-read is not this function's concern. No timer and no
 * proactive call of any kind -- this only ever runs in response to a
 * route actually mounting, which is this feature's definition of "use"
 * for AC-9's "refresh only on use" rule (see token.ts's own header
 * comment).
 */
export function bootstrapSessionFromRefreshCookie(): Promise<boolean> {
  return refreshOnce();
}

/**
 * Authenticated fetch wrapper: attaches the in-memory JWT as a bearer
 * token and, on a 401, shares a single refresh-and-retry sequence
 * (`refreshOnce`) with every other concurrent call before retrying the
 * request exactly once more. A second 401 after a successful refresh is
 * returned to the caller as-is -- this function never loops. A failed
 * refresh (session ended, or every retry exhausted) returns the
 * ORIGINAL 401 rather than attempting the request again with a token
 * already known to be invalid.
 */
export async function authenticatedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const run = (): Promise<Response> => {
    const token = getSessionToken();
    const headers = new Headers(init.headers);
    if (token !== null) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return fetch(input, { ...init, credentials: "include", headers });
  };

  const first = await run();
  if (first.status !== 401) {
    return first;
  }

  const refreshed = await refreshOnce();
  if (!refreshed) {
    return first;
  }
  return run();
}

export type SubmitRenewalOutcome = "submitted" | "role_not_permitted" | "error";

export interface SubmitRenewalResult {
  outcome: SubmitRenewalOutcome;
}

/**
 * POST /api/portal/submission (AC-10, AC-12, AC-27). Added at the
 * portal-auth client boundary now (ADR-20261008-jg8z); wiring
 * `DeclarationRoute`'s submit handler to this function is tracked debt
 * (ADR-20261007-wkxq) because the journey's own data ports are still
 * stubs (D-22) -- calling it from there now would cross
 * `m1-portal-routing-resume-model` mid-flight. The renewal comes from the
 * session claim the server validates, never from this call's own
 * arguments (AC-12: "never the request body").
 */
export async function submitRenewal(): Promise<SubmitRenewalResult> {
  let response: Response;
  try {
    response = await authenticatedFetch("/api/portal/submission", {
      method: "POST",
      headers: JSON_HEADERS,
    });
  } catch {
    return { outcome: "error" };
  }

  if (response.status === 200) {
    return { outcome: "submitted" };
  }
  if (response.status === 403) {
    const code = readStringField(await readJsonBody(response), "code");
    if (code === "role_not_permitted_to_submit") {
      return { outcome: "role_not_permitted" };
    }
  }
  return { outcome: "error" };
}
